/*
 *  Enums
 *
 * */
import {
  AccountingCustomerResponseBody,
  Alpha3,
  CashRegisterDocumentItemResponseBody,
  CashRegisterDocumentKindOfPaymentEnum,
  CashRegisterDocumentStateEnum,
  CashRegisterDocumentTypeEnum,
  CreateCashRegisterDocumentItemRequestBody,
  CustomFieldValue,
  Decimal,
  FieldValue,
  IssuerResponseBody,
  Money,
  MoneyResponseBody,
  PaymentInfoResponseBody,
  PaymentType,
  PhoneNumber,
  PriceResponseBody,
  RoundingDefinitionAndValidityResponseBody,
  SupplierResponseBody,
  TaxDocumentCalculationSummaryItemResponseBody,
  TaxDocumentCalculationV2Response,
  UuidResponseBody,
  VatCheckTotalsDefinitionDocumentTypeEnum,
  VatType,
} from '@dms/api/shared';

import {Nullish} from 'shared/web';

export type GetStockMovementsRequestMetadaAccounting = void;
export type GetStockMovementsResponseMetadaAccounting = {
  stockMovementType: string;
  name: string;
};

export type GetOperationalItemsRequestMetadaAccounting = {
  accountingDate?: string;
  application: 'workshop' | 'used-cars';
  itemGroup?: string;
};
export type GetOperationalItemsResponseMetadaAccounting = {
  applicationId: string;
  itemCodeName: string;
  itemCode: string;
  operationId: string;
  itemGroup: string;
  isGroupDefault: boolean;
};

export type PayWithBankTransferResMetadaAccounting = void;
export type PayWithBankTransferArgMetadaAccounting = {
  invoiceId: string;
  paymentDate: string;
  amount: Money;
};
export type PayWithCardResMetadaAccounting = void;
export type PayWithCardArgMetadaAccounting = {
  invoiceId: string;
  paymentDate: string;
  amount: Money;
};
export type PayWithCardReceiptResMetadaAccounting = void;
export type PayWithCardReceiptArgMetadaAccounting = {
  invoiceId: string;
  paymentDate: string;
  amount: Money;
  cashRegisterId: string;
  cashRegisterDocumentId: string;
  billingInformationId: string;
  description: string | null;
  exchangeRateRatio?:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};
export type PayWithCashResMetadaAccounting = void;
export type PayWithCashArgMetadaAccounting = {
  invoiceId: string;
  cashRegisterId: string;
  paymentDate: string;
  amount: Money;
};
export type PayWithCashReceiptResMetadaAccounting = void;
export type PayWithCashReceiptArgMetadaAccounting = {
  invoiceId: string;
  paymentDate: string;
  amount: Money;
  cashRegisterId: string;
  cashRegisterDocumentId: string;
  billingInformationId: string;
  description: string | null;
  exchangeRateRatio?:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};

export type InvoicingDocumentTypeMetadaAccounting =
  | 'invoice'
  | 'balance_invoice'
  | 'invoice_proforma'
  | 'tax_document_for_payment';
export type PaymentInfoRequestBodyMetadaAccounting = {
  paymentMethod: 'BANK_TRANSFER' | 'CARD' | 'CASH' | 'OFFSET';
  bankName?: string | null;
  bankAccount?: string | null;
  iban?: string | null;
  swift?: string | null;
  variableSymbol?: string | null;
  constantSymbol?: string | null;
  specificSymbol?: string | null;
};
export type CorrectiveTaxDocumentItemRequestBody = {
  name: string;
  quantity: Decimal;
  unit: string;
  pricePerUnit: string;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
  currency: Alpha3;
};
export type CreateCorrectiveTaxDocumentRequestBody = {
  documentSeriesId: string;
  invoicingDocumentId: string;
  invoicingDocumentType: InvoicingDocumentTypeMetadaAccounting;
  issueDate: string;
  dueDate: string;
  dateOfTaxableSupply: string;
  dateOfReception: string;
  description: string;
  reason: string;
  behindItemsNote: string | null;
  internalNote: string | null;
  paymentInfo: PaymentInfoRequestBodyMetadaAccounting;
  additionalCustomer:
    | {
        customerId: string;
        contractInformationId: string;
        type: 'operator' | 'recipient';
      }
    | Nullish;
  items: InvoicingDocumentItemRequestBodyMetadaAccounting[];
  exchangeRateRatio:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
  customFields: CustomFieldValue[];
};
export type VatPriceResponseBodyMetadaAccounting = {
  total: Money;
  base: Money;
  vatRate: string;
  vatType: VatType;
  vat: Money;
};
export type CorrectiveTaxDocumentItemResponseBodyMetadaAccounting = {
  referenceId: string;
  name: string;
  quantity: string;
  unit: string;
  pricePerUnit: MoneyResponseBody;
  isUnitPriceWithVat: boolean;
  totalPrice: VatPriceResponseBodyMetadaAccounting;
};
export type RenderedTemplateResponseBodyMetadaAccounting = {
  url: string;
  pdfUrl: string;
};
export type RenderBalanceInvoiceDocumentRequestBodyMetadaAccounting = {
  documentId: string;
  isMarginPrint?: boolean;
};
export type RenderCorrectiveTaxDocumentRequestBodyMetadaAccounting = {
  documentId: string;
  isMarginPrint?: boolean;
};
export type RenderExpenseCashReceiptDocumentRequestBodyMetadaAccounting = {
  cashRegisterDocumentId: string;
};
export type RenderIncomeCashReceiptDocumentRequestBodyMetadaAccounting = {
  cashRegisterDocumentId: string;
};
export type RenderInvoiceDocumentRequestBodyMetadaAccounting = {
  documentId: string;
  isMarginPrint?: boolean;
};
export type RenderInvoiceProformaDocumentRequestBodyMetadaAccounting = {
  documentId: string;
  isMarginPrint?: boolean;
};
export type RenderTaxDocumentForPaymentDocumentRequestBodyMetadaAccounting = {
  documentId: string;
  isMarginPrint?: boolean;
};

export type InvoicingDocumentTypeUnionMetadaAccounting =
  | 'invoice'
  | 'balance_invoice'
  | 'invoice_proforma'
  | 'tax_document_for_payment'
  | 'invoice_draft'
  | 'corrective_tax_document';
export type DocumentSeriesDefinitionResponseBodyMetadaAccounting = {
  id: string;
  name: string;
  documentSeriesId: string;
  invoicingDocumentType: InvoicingDocumentTypeUnionMetadaAccounting;
  branchId: string | null;
  isSystem: boolean;
};
export type DocumentSeriesDefinitionRequestBodyMetadaAccounting = {
  name: string;
  documentSeriesId: string;
  branchId: string | null;
  invoicingDocumentType: 'invoice' | 'corrective_tax_document';
};
export type InvoiceItemResponseBodyMetadaAccounting = {
  referenceId: string;
  name: string;
  quantity: string;
  unit: string;
  pricePerUnit: MoneyResponseBody;
  isUnitPriceWithVat: boolean;
  totalPrice: VatPriceResponseBodyMetadaAccounting;
  type: 'standard' | 'proforma_deduction';
};
export type InvoicePaymentResponseBodyMetadaAccounting = {
  amount: Money;
  paymentId: string;
  transactionId: string;
  paymentType: PaymentType;
  source: 'internal' | 'external';
  paymentDate: string;
  cashRegisterDocumentId: string | null;
  cashRegisterDocumentNumber: string | null;
  cancelledTransactionId: string | null;
  cancelingTransactionId: string | null;
  correctiveTaxDocumentForTaxDocumentForPaymentId: string | null;
  correctiveTaxDocumentForTaxDocumentForPaymentNumber: string | null;
};

export type AccountingCustomerRequestBodyMetadaAccounting = {
  id: string;
  name: string | null;
  tradeName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
};

export type SupplierDraftResponseBodyMetadaAccounting = {
  id: string | null;
  name: string | null;
  tradeName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
  fileNumber: string | null;
};
export type CustomerDraftResponseBodyMetadaAccounting = {
  id: string;
  name: string | null;
  tradeName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
  printLanguage: string | null;
};
export type PaymentInfoDraftResponseBodyMetadaAccounting = {
  paymentMethod: ('BANK_TRANSFER' | 'CARD' | 'CASH' | 'OFFSET') | null;
  bankAccount: string | null;
  iban: string | null;
  swift: string | null;
  variableSymbol: string | null;
  constantSymbol: string | null;
  specificSymbol: string | null;
};
export type InvoiceDraftResponseBodyMetadaAccounting = {
  id: string;
  offerId: string | null;
  templateId: string | null;
  issueDate: string | null;
  dueDate: string | null;
  createdAt: string;
  author: string;
  dateOfTaxableSupply: string | null;
  dueSinceIssueDateInDays: number | null;
  branchId: string;
  currency: Alpha3 | null;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  additionalCustomer: {
    customerId: string;
    contractInformationId: string;
    type: 'operator' | 'recipient';
    name: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    zip: string;
    country: string;
    cin: string;
    tin: string;
    phoneNumber: {
      countryCode: string;
      prefix: string;
      number: string;
    };
  } | null;
  supplier: SupplierDraftResponseBodyMetadaAccounting;
  customer: CustomerDraftResponseBodyMetadaAccounting;
  paymentInfo: PaymentInfoDraftResponseBodyMetadaAccounting;
  type: 'DRAFT';
  state: 'IN_PROGRESS';
  exchangeRateRatio:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};
export type InvoiceDraftItemRequestBodyMetadaAccounting = {
  name?: string;
  quantity?: Decimal;
  unit?: string;
  pricePerUnit?: string;
  isUnitPriceWithVat?: boolean;
  vatType?: VatType;
  currency?: Alpha3;
};
export type InvoiceDraftRequestBodyMetadaAccounting = {
  offerId?: string;
  templateId?: string;
  issueDate?: string;
  dueDate?: string;
  dateOfTaxableSupply?: string;
  currency?: Alpha3;
  note?: string | null;
  footerNote?: string | null;
  internalNote?: string | null;
  invoiceItems?: InvoiceDraftItemRequestBodyMetadaAccounting[];
  billingInformationId: string;
  branchId: string;
  customer?: AccountingCustomerRequestBodyMetadaAccounting;
  additionalCustomer:
    | {
        customerId: string;
        contractInformationId: string;
        type: 'operator' | 'recipient';
      }
    | Nullish;
  paymentInfo?: {
    paymentMethod: 'BANK_TRANSFER' | 'CARD' | 'CASH' | 'OFFSET';
    bankName?: string | null;
    bankAccount?: string | null;
    iban?: string | null;
    swift?: string | null;
    variableSymbol?: string | null;
    constantSymbol?: string | null;
    specificSymbol?: string | null;
  };
  exchangeRateRatio:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};

export type MoneyDraftResponseBodyMetadaAccounting = {
  amount?: Decimal;
  currency?: Alpha3;
};
export type InvoiceDraftItemResponseBodyMetadaAccounting = {
  name: string | null;
  quantity: Decimal | null;
  unit: string | null;
  pricePerUnit: MoneyDraftResponseBodyMetadaAccounting | null;
  isUnitPriceWithVat: boolean | null;
  vatType: string | null;
  currency: string | null;
};
export type PaymentStateTypeMetadaAccounting =
  | 'draft'
  | 'canceled'
  | 'completed'
  | 'confirmed'
  | 'partially_paid'
  | 'overpayment'
  | 'reopened'
  | 'canceling'
  | 'overdue';

export type InvoiceProformaResponseBodyMetadaAccounting = {
  id: string;
  offerId: string | null;
  templateId: string;
  number: string;
  type: 'ISSUED' | 'ACCEPTED' | 'PROMO';
  paymentState: PaymentStateTypeMetadaAccounting;
  paymentPrescriptionId: string;
  branchId: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string;
  additionalCustomer: {
    customerId: string;
    contractInformationId: string;
    type: 'operator' | 'recipient';
    name: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    zip: string;
    country: string;
    cin: string;
    tin: string;
    phoneNumber: {
      countryCode: string;
      prefix: string;
      number: string;
    };
  } | null;
  dueSinceIssueDateInDays: number;
  issueDate: string;
  author: string;
  createdAt: string;
  sentAt: string | null;
  cancelledAt: string | null;
  cancellable: boolean;
  cancelledDocumentId: string | null;
  supplier: SupplierResponseBody;
  customer: AccountingCustomerResponseBody;
  paymentInfo: PaymentInfoResponseBody;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  invoiceSummary: TaxDocumentCalculationResponseBody;
  exchangeRateRatioInvoiceSummary: TaxDocumentCalculationResponseBody;
  exchangeRateRatio:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
  customFieldsPayload: CustomFieldValue[];
};

export type InvoiceProformaItemResponseBodyMetadaAccounting = {
  referenceId: string;
  name: string;
  quantity: string;
  unit: string;
  pricePerUnit: MoneyResponseBody;
  isUnitPriceWithVat: boolean;
  totalPrice: VatPriceResponseBodyMetadaAccounting;
};
export type InvoiceProformaPaymentResponseBodyMetadaAccounting = {
  amount: Money;
  paymentId: string;
  transactionId: string;
  paymentType: PaymentType;
  paymentDate: string;
  source: 'internal' | 'external';
  cashRegisterDocumentId: string | null;
  cashRegisterDocumentNumber: string | null;
  taxDocumentForPaymentId: string | null;
  taxDocumentForPaymentNumber: string | null;
  cancelledTransactionId: string | null;
  cancelingTransactionId: string | null;
  correctiveTaxDocumentForTaxDocumentForPaymentId: string | null;
  correctiveTaxDocumentForTaxDocumentForPaymentNumber: string | null;
};

export type GetCorrectiveTaxDocumentItemsApiResponseMetadaAccounting =
  CorrectiveTaxDocumentItemResponseBodyMetadaAccounting[];
export type GetCorrectiveTaxDocumentItemsApiArgMetadaAccounting = {
  correctiveTaxDocumentId: string;
};
export type SendCorrectiveTaxDocumentApiResponseMetadaAccounting = unknown;
export type SendCorrectiveTaxDocumentApiArgMetadaAccounting = {
  correctiveTaxDocumentId: string;
};
export type RenderBalanceInvoiceDocumentApiResponseMetadaAccounting =
  RenderedTemplateResponseBodyMetadaAccounting;
export type RenderBalanceInvoiceDocumentApiArgMetadaAccounting = {
  renderBalanceInvoiceDocumentRequestBody: RenderBalanceInvoiceDocumentRequestBodyMetadaAccounting;
};
export type RenderCorrectiveTaxDocumentApiResponseMetadaAccounting =
  RenderedTemplateResponseBodyMetadaAccounting;
export type RenderCorrectiveTaxDocumentApiArgMetadaAccounting = {
  renderCorrectiveTaxDocumentRequestBody: RenderCorrectiveTaxDocumentRequestBodyMetadaAccounting;
};
export type RenderExpenseCashReceiptDocumentApiResponseMetadaAccounting =
  RenderedTemplateResponseBodyMetadaAccounting;
export type RenderExpenseCashReceiptDocumentApiArgMetadaAccounting = {
  renderExpenseCashReceiptDocumentRequestBody: RenderExpenseCashReceiptDocumentRequestBodyMetadaAccounting;
};
export type RenderIncomeCashReceiptDocumentApiResponseMetadaAccounting =
  RenderedTemplateResponseBodyMetadaAccounting;
export type RenderIncomeCashReceiptDocumentApiArgMetadaAccounting = {
  renderIncomeCashReceiptDocumentRequestBody: RenderIncomeCashReceiptDocumentRequestBodyMetadaAccounting;
};
export type RenderInvoiceDocumentApiResponseMetadaAccounting =
  RenderedTemplateResponseBodyMetadaAccounting;
export type RenderInvoiceDocumentApiArgMetadaAccounting = {
  renderInvoiceDocumentRequestBody: RenderInvoiceDocumentRequestBodyMetadaAccounting;
};
export type RenderInvoiceProformaDocumentApiResponseMetadaAccounting =
  RenderedTemplateResponseBodyMetadaAccounting;
export type RenderInvoiceProformaDocumentApiArgMetadaAccounting = {
  renderInvoiceProformaDocumentRequestBody: RenderInvoiceProformaDocumentRequestBodyMetadaAccounting;
};
export type RenderTaxDocumentForPaymentDocumentApiResponseMetadaAccounting =
  RenderedTemplateResponseBodyMetadaAccounting;
export type RenderTaxDocumentForPaymentDocumentApiArgMetadaAccounting = {
  renderTaxDocumentForPaymentDocumentRequestBody: RenderTaxDocumentForPaymentDocumentRequestBodyMetadaAccounting;
};
export type CancelInvoiceApiResponseMetadaAccounting = /** status 204  */ undefined;
export type CancelInvoiceApiArgMetadaAccounting = {
  invoiceId: string;
};
export type CreateDocumentSeriesDefinitionApiResponseMetadaAccounting =
  DocumentSeriesDefinitionResponseBodyMetadaAccounting;
export type CreateDocumentSeriesDefinitionApiArgMetadaAccounting = {
  documentSeriesDefinitionRequestBody: DocumentSeriesDefinitionRequestBodyMetadaAccounting;
};
export type GetDocumentSeriesDefinitionListApiResponseMetadaAccounting =
  DocumentSeriesDefinitionResponseBodyMetadaAccounting[];
export type GetDocumentSeriesDefinitionListApiArgMetadaAccounting = {};

export type ReprocessErrorLogApiArgMetadaAccounting = {
  documentId: string;
  errorLogId: string;
};

export type ReprocessErrorLogApiResMetadaAccounting = void;
export type GetErrorLogApiResMetadaAccounting = {
  id: string | Nullish;
  createdAt: string | Nullish;
  documentId: string | Nullish;
  documentType: string | Nullish;
  interface: string | Nullish;
  operation: string | Nullish;
  errorMessage: string | Nullish;
  notes: string | Nullish;
};
export type GetErrorLogApiArgMetadaAccounting = {id: string};

export type DeleteDocumentSeriesDefinitionApiResponseMetadaAccounting =
  /** status 204  */ undefined;
export type DeleteDocumentSeriesDefinitionApiArgMetadaAccounting = {
  documentSeriesDefinitionId: string;
};
export type UpdateDocumentSeriesDefinitionApiResponseMetadaAccounting =
  DocumentSeriesDefinitionResponseBodyMetadaAccounting;
export type UpdateDocumentSeriesDefinitionApiArgMetadaAccounting = {
  documentSeriesDefinitionId: string;

  documentSeriesDefinitionRequestBody: DocumentSeriesDefinitionRequestBodyMetadaAccounting;
};
export type TaxDocumentCalculationItemRequestBodyMetadaAccounting = {
  quantity: string;
  reference: string;
  unitPrice: string;
  relatedItemId?: string;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
};
export type GetCategoryApiArgMetadaAccounting = {
  category:
    | 'matrices-config-from-metada-to-google'
    | 'matrices-config-from-google-to-metada'
    | 'application'
    | 'abra-mapping-method';
};
export type GetCategoryApiResMetadaAccounting = {
  category: string | null;
  code: string | null;
  name: string | null;
  priority: string | null;
}[];
export type AccPostConfigurationPaymentImportModeApiResponseMetadaAccounting =
  /** status 204 Standard response */ undefined;

export type PaymentImportModeMetadaAccounting =
  | 'importTrxOnly'
  | 'importTrxDmsCreateTaxDocument'
  | 'importTaxDocumentOnly';
export type AccPostConfigurationPaymentImportModeApiArgMetadaAccounting = {
  body: {
    branchId: string;
    paymentImportMode: PaymentImportModeMetadaAccounting;
  } | null;
};
export type AccPatchConfigurationPaymentImportModeApiResponseMetadaAccounting =
  /** status 204 Standard response */ undefined;
export type AccPatchConfigurationPaymentImportModeApiArgMetadaAccounting = {
  branchId: string | null;
  body: {
    paymentImportMode: PaymentImportModeMetadaAccounting;
  } | null;
};

export type AccGetConfigurationPaymentImportModeApiResponseMetadaAccounting =
  | /** status 200 Standard response */ ({
      branchId?: string | null;
      paymentImportMode?: PaymentImportModeMetadaAccounting | null;
    } | null)
  | /** status 204 Standard response */ undefined;
export type AccGetConfigurationPaymentImportModeApiArgMetadaAccounting = {
  branchId: string;
};
export type AccDeleteConfigurationPaymentImportModeApiResponseMetadaAccounting =
  /** status 204 Standard response */ undefined;
export type AccDeleteConfigurationPaymentImportModeApiArgMetadaAccounting = {
  branchId: string;
};
export type MoveMatricesApiArgMetadaAccounting = {
  category: 'matrices-config-from-metada-to-google' | 'matrices-config-from-google-to-metada';
  tables: string[];
};
export type MoveAllMatricesFromCarisApiArgMetadaAccounting = {
  googleDocId: string;
  sheetName: string;
  validFrom: string;
  validTo: string;
};
export type MoveAllMatricesFromCarisApiResMetadaAccounting = unknown;
export type MoveMatricesApiResMetadaAccounting = unknown;

export type MatricesConfigCleanupApiArgMetadaAccounting = {
  tables: string[];
};
export type MatricesConfigCleanupApiResMetadaAccounting = unknown;

export type AccountingDocumentRecalculateApiArgMetadaAccounting = {
  country: string;
  currency: string;
  dueDate: string;
  items: {
    itemId: string;
    unit: string;
    relatedItemId: string | null;
    quantity: string;
    unitPrice: string;
    unitType: 'standard' | 'percent';
    isUnitPriceWithVat: boolean;
    vatType: VatType;
    type: InvoicingDocumentAccountingItemTypeMetadaAccounting;
    description: string;
    currency: string;
  }[];
  branchId: string | null;
  paymentType: PaymentType;
  vatCheckTotalsDocumentType: VatCheckTotalsDefinitionDocumentTypeEnum;
  exchangeRateRatio:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};

export type AccountingDocumentRecalculateItemResMetadaAccounting = {
  itemId: string;
  relatedItemId: string | null;
  quantity: string;
  unitPrice: string;
  unit: string;
  unitType: 'standard' | 'percent';
  priceWithVat: string;
  priceWithoutVat: string;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
  type: InvoicingDocumentAccountingItemTypeMetadaAccounting;
  description: string;
  currency: string;
  discount: InvoicingDocumentItemDiscountRequestBodyMetadaAccounting;
};
export type AccountingDocumentRecalculateApiResMetadaAccounting = {
  items: AccountingDocumentRecalculateItemResMetadaAccounting[];
  documentSummary: {
    calculation: TaxDocumentCalculationV2Response;
    exchangeRateRatioCalculation: TaxDocumentCalculationV2Response;
  };
};

export type TaxDocumentForPaymentItemResponseBodyMetadaAccounting = {
  referenceId: string;
  name: string;
  quantity: string;
  unit: string;
  pricePerUnit: MoneyResponseBody;
  isUnitPriceWithVat: boolean;
  totalPrice: VatPriceResponseBodyMetadaAccounting;
};
export type TaxDocumentForPaymentResponseBodyMetadaAccounting = {
  id: string;
  invoiceProformaId: string;
  userId: string;
  templateId: string;
  number: string;
  createdAt: string;
  issueDate: string;
  dueDate: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  internalNote: string | null;
  documentItems: TaxDocumentForPaymentItemResponseBodyMetadaAccounting[];
  supplier: SupplierResponseBody;
  customer: AccountingCustomerResponseBody;
  paymentInfo: PaymentInfoResponseBody;
  paymentPrescriptionId: string;
  branchId: string;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  taxDocumentSummary: TaxDocumentCalculationResponseBody;
  exchangeRateRatioInvoiceSummary: TaxDocumentCalculationResponseBody;
  customFieldsPayload: CustomFieldValue[];
  paymentState: never;
  cancellable: boolean;
  source: 'internal' | 'external';
  exchangeRateRatio:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};

export type TaxDocumentCalculationRequestBodyMetadaAccounting = {
  country: string;
  currency: string;
  taxableSupplyDate: string;
  items: TaxDocumentCalculationItemRequestBodyMetadaAccounting[];
  branchId: string | null;
  paymentType: PaymentType;
  vatCheckTotalsDocumentType: VatCheckTotalsDefinitionDocumentTypeEnum;
  exchangeRateRatio?:
    | {
        amount: number;
        ratio: string;
        currency: string;
      }
    | Nullish;
};

export type ChangeInvoiceCustomerApiArgMetadaAccounting = {
  invoiceId: string;
  id: string;
  name: string | null;
  tradeName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
};

export type GetInvoiceItemsApiResponseMetadaAccounting = InvoiceItemResponseBodyMetadaAccounting[];
export type GetInvoiceItemsApiArgMetadaAccounting = {
  invoiceId: string;
};
export type GetInvoicePaymentListApiResponse = InvoicePaymentResponseBodyMetadaAccounting[];
export type GetInvoicePaymentListApiArg = {
  invoiceId: string;
};

export type GetCorrectiveTaxDocumentPaymentListApiArg = {
  correctiveTaxDocumentId: string;
};
export type GetCorrectiveTaxDocumentPaymentListApiResponse =
  InvoicePaymentResponseBodyMetadaAccounting[];

export type SendInvoiceApiResponseMetadaAccounting = /** status 204  */ undefined;
export type SendInvoiceApiArgMetadaAccounting = {
  invoiceId: string;
};
export type DeleteInvoiceDraftApiResponseMetadaAccounting = /** status 204  */ undefined;
export type DeleteInvoiceDraftApiArgMetadaAccounting = {
  invoiceDraftId: string;
};

export type PutInvoiceDraftApiResponseMetadaAccounting = InvoiceDraftResponseBodyMetadaAccounting;
export type PutInvoiceDraftApiArgMetadaAccounting = {
  invoiceDraftId: string;

  invoiceDraftRequestBody: InvoiceDraftRequestBodyMetadaAccounting;
};

export type InvoiceTypesMetadaAccounting =
  | 'DRAFT'
  | 'ISSUED'
  | 'CORRECTIVE_TAX_DOCUMENT'
  | 'BALANCE_INVOICE'
  | 'TAX_DOCUMENT_FOR_PAYMENT'
  | 'PROFORMA';

export type GetInvoiceDraftItemsApiResponseMetadaAccounting =
  InvoiceDraftItemResponseBodyMetadaAccounting[];
export type GetInvoiceDraftItemsApiArgMetadaAccounting = {
  invoiceDraftId: string;
};
export type PostInvoiceDraftApiResponseMetadaAccounting = InvoiceDraftResponseBodyMetadaAccounting;
export type PostInvoiceDraftApiArgMetadaAccounting = {
  invoiceDraftRequestBody: InvoiceDraftRequestBodyMetadaAccounting;
};
export type CancelInvoiceProformaApiArgMetadaAccounting = {
  invoiceProformaId: string;
};
export type CancelInvoiceProformaApiResponseMetadaAccounting = unknown;

export type GetInvoiceProformaItemsApiResponseMetadaAccounting =
  InvoiceProformaItemResponseBodyMetadaAccounting[];
export type GetInvoiceProformaItemsApiArgMetadaAccounting = {
  invoiceId: string;
};
export type GetInvoiceProformaPaymentListApiResponse =
  InvoiceProformaPaymentResponseBodyMetadaAccounting[];
export type GetInvoiceProformaPaymentListApiArg = {
  invoiceId: string;
};
export type PaymentTypeResponseBodyMetadaAccounting = {
  key: string;
  value: string;
};
export type GetPaymentTypesForInvoicePaymentApiResponseMetadaAccounting =
  PaymentTypeResponseBodyMetadaAccounting[];
export type GetPaymentTypesForInvoicePaymentApiArgMetadaAccounting = Record<string, never>;

export type GetPaymentTypeEnumApiResponseMetadaAccounting =
  PaymentTypeResponseBodyMetadaAccounting[];

export type TaxDocumentCalculationApiArgMetadaAccounting = {
  taxDocumentCalculationRequestBody: TaxDocumentCalculationRequestBodyMetadaAccounting;
};

export type GetBalanceInvoiceByInvoiceProformaIdApiResponseMetadaAccounting =
  BalanceInvoiceV2ResponseBodyMetadaAccounting;
export type GetBalanceInvoiceByInvoiceProformaIdApiArgMetadaAccounting = {
  invoiceProformaId: string;
};
export type GetBalanceInvoiceApiResponseMetadaAccounting =
  BalanceInvoiceV2ResponseBodyMetadaAccounting;
export type GetBalanceInvoiceApiArgMetadaAccounting = {
  balanceInvoiceId: string;
};
export type GetCorrectiveTaxDocumentApiResponseMetadaAccounting =
  CorrectiveTaxDocumentV3ResponseBodyMetadaAccounting;

export type CheckoutCorrectiveTaxDocumentTypeMetadaAccounting = 'partial' | 'full';
export type GetCorrectiveTaxDocumentApiArgMetadaAccounting = {
  correctiveTaxDocumentId: string;
};
export type GetInvoiceApiResponseMetadaAccounting = InvoiceV3ResponseBodyMetadaAccounting;
export type GetInvoiceApiArgMetadaAccounting = {
  invoiceId: string;
};
export type CreateInvoiceDraftApiResponseMetadaAccounting = {
  id: string;
};
export type CreateInvoiceDraftApiArgMetadaAccounting = {
  invoiceDraftV2RequestBody: InvoiceDraftV2RequestBodyMetadaAccounting;
};
export type GetInvoiceDraftApiResponseMetadaAccounting = InvoiceDraftV2ResponseBodyMetadaAccounting;
export type GetInvoiceDraftApiArgMetadaAccounting = {
  invoiceDraftId: string;
};
export type UpdateInvoiceDraftApiResponseMetadaAccounting = unknown;
export type UpdateInvoiceDraftApiArgMetadaAccounting = {
  id: string;

  invoiceDraftV2RequestBody: InvoiceDraftV2RequestBodyMetadaAccounting;
};
export type CreateInvoiceProformaApiResponseMetadaAccounting = {
  id: string;
};
export type CreateInvoiceProformaApiArgMetadaAccounting = {
  invoiceProformaV2RequestBody: InvoiceProformaV2RequestBodyMetadaAccounting;
};
export type GetInvoiceProformaApiResponseMetadaAccounting =
  InvoiceProformaV2ResponseBodyMetadaAccounting;
export type GetInvoiceProformaApiArgMetadaAccounting = {
  invoiceProformaId: string;
};
export type PayWithBankTransferApiResponseMetadaAccounting = unknown;
export type PayWithBankTransferApiArgMetadaAccounting = {
  correctiveTaxDocumentId: string;
  paymentDate: string;
  amount: Money;
};
export type PayWithCardAndCreateReceiptApiArgMetadaAccounting = {
  correctiveTaxDocumentId: string;
  paymentDate: string;
  cashRegisterId: string;
  cashRegisterDocumentId: string;
  amount: Money;
  billingInformationId: string;
  description: string | null;
  exchangeRateRatio?: ExchangeRateRatioBodyMetadaAccounting;
};
export type PayWithCardApiArgMetadaAccounting = {
  correctiveTaxDocumentId: string;
  paymentDate: string;
  amount: Money;
};
export type PayWithCashAndCreateReceiptApiArgMetadaAccounting = {
  correctiveTaxDocumentId: string;
  paymentDate: string;
  cashRegisterId: string;
  cashRegisterDocumentId: string;
  amount: Money;
  billingInformationId: string;
  description: string | null;
  exchangeRateRatio?: ExchangeRateRatioBodyMetadaAccounting;
};

export type CreateTaxDocumentSettingApiResponseMetadaAccounting =
  TaxDocumentSettingResponseBodyMetadaAccounting;
export type CreateTaxDocumentSettingApiArgMetadaAccounting = {
  taxDocumentSettingRequestBody: TaxDocumentSettingRequestBodyMetadaAccounting;
};
export type DeleteTaxDocumentSettingApiResponseMetadaAccounting = unknown;
export type DeleteTaxDocumentSettingApiArgMetadaAccounting = {
  id: string;
};
export type GetTaxDocumentSettingApiResponseMetadaAccounting =
  TaxDocumentSettingResponseBodyMetadaAccounting;
export type GetTaxDocumentSettingListApiResponseMetadaAccounting =
  TaxDocumentSettingResponseBodyMetadaAccounting[];
export type GetTaxDocumentSettingListApiArgMetadaAccounting = void;
export type GetTaxDocumentSettingApiArgMetadaAccounting = {
  id: string;
};
export type UpdateTaxDocumentSettingApiResponseMetadaAccounting =
  TaxDocumentSettingResponseBodyMetadaAccounting;
export type UpdateTaxDocumentSettingApiArgMetadaAccounting = {
  id: string;

  updateTaxDocumentSettingRequestBody: UpdateTaxDocumentSettingRequestBodyMetadaAccounting;
};
export type VatCalculationTypeMetadaAccounting = 'from-above' | 'from-below' | 'sum';
export type VatCheckTotalsDocumentTypeV2MetadaAccounting =
  | 'cash-receipts'
  | 'invoicing-documents'
  | 'invoices'
  | 'off';
export type CreateVatTotalAndCalculationSettingRequestBodyMetadaAccounting = {
  branchId: string;
  calculationType: VatCalculationTypeMetadaAccounting;
  documentType: VatCheckTotalsDocumentTypeV2MetadaAccounting | null;
};
export type VatTotalAndCalculationSettingResponseBodyMetadaAccounting = {
  id: string;
  branchId: string | null;
  calculationType: VatCalculationTypeMetadaAccounting;
  documentType: VatCheckTotalsDocumentTypeV2MetadaAccounting | null;
  isSystem: boolean;
};
export type UpdateVatTotalAndCalculationSettingRequestBodyMetadaAccounting = {
  calculationType: VatCalculationTypeMetadaAccounting;
  documentType: VatCheckTotalsDocumentTypeV2MetadaAccounting | null;
  vatTotalAndCalculationId: string;
};
export type CreateVatTotalAndCalculationSettingApiResponseMetadaAccounting = object;
export type CreateVatTotalAndCalculationSettingApiArgMetadaAccounting =
  CreateVatTotalAndCalculationSettingRequestBodyMetadaAccounting;

export type DeleteVatTotalAndCalculationSettingApiResponseMetadaAccounting = unknown;
export type DeleteVatTotalAndCalculationSettingApiArgMetadaAccounting = {
  vatTotalAndCalculationId: string;
};
export type GetVatTotalAndCalculationSettingApiResponseMetadaAccounting =
  VatTotalAndCalculationSettingResponseBodyMetadaAccounting;
export type GetVatTotalAndCalculationSettingApiArgMetadaAccounting = {
  vatTotalAndCalculationId: string;
};
export type UpdateVatTotalAndCalculationSettingApiResponseMetadaAccounting =
  VatTotalAndCalculationSettingResponseBodyMetadaAccounting;
export type UpdateVatTotalAndCalculationSettingApiArgMetadaAccounting =
  UpdateVatTotalAndCalculationSettingRequestBodyMetadaAccounting;
export type GetListVatTotalAndCalculationSettingApiResponseMetadaAccounting =
  VatTotalAndCalculationSettingResponseBodyMetadaAccounting[];
export type GetListVatTotalAndCalculationSettingApiArgMetadaAccounting = void;
export type IssuedTypeMetadaAccounting = 'automatic' | 'manual' | 'not_create';
export type TaxDocumentSettingResponseBodyMetadaAccounting = {
  id: string;
  branchId: string | null;
  issuedType: IssuedTypeMetadaAccounting;
  isSystem: boolean;
};
export type TaxDocumentSettingRequestBodyMetadaAccounting = {
  branchId: string;
  issuedType: IssuedTypeMetadaAccounting;
};
export type UpdateTaxDocumentSettingRequestBodyMetadaAccounting = {
  branchId: string | null;
  issuedType: IssuedTypeMetadaAccounting;
};

export type PayWithCashApiResponseMetadaAccounting = unknown;
export type PayWithCashApiArgMetadaAccounting = {
  correctiveTaxDocumentId: string;
  paymentDate: string;
  cashRegisterId: string | null;
  amount: Money;
};

export type PayWithCardAndCreateReceiptApiResponseMetadaAccounting = unknown;
export type PayWithCardApiResponseMetadaAccounting = unknown;
export type PayWithCashAndCreateReceiptApiResponseMetadaAccounting = unknown;

export type CreateDiscountItemApiResponseMetadaAccounting =
  InvoicingDocumentItemRequestBodyMetadaAccounting;
export type CreateDiscountItemApiArgMetadaAccounting = {
  createInvoicingItemDiscountRequestBody: CreateInvoicingItemDiscountRequestBodyMetadaAccounting;
};
export type CreateMarginItemApiResponseMetadaAccounting =
  InvoicingDocumentItemRequestBodyMetadaAccounting;
export type CreateMarginItemApiArgMetadaAccounting = {
  createInvoicingItemMarginRequestBody: CreateInvoicingItemMarginRequestBodyMetadaAccounting;
};
export type CreateStandardItemApiResponseMetadaAccounting =
  InvoicingDocumentItemRequestBodyMetadaAccounting;
export type CreateStandardItemApiArgMetadaAccounting = {
  createInvoicingItemStandardRequestBody: CreateInvoicingItemStandardRequestBodyMetadaAccounting;
};
export type CreateStandardMultipleItemsApiResponseMetadaAccounting =
  InvoicingDocumentItemRequestBodyMetadaAccounting[];
export type CreateStandardMultipleItemsApiArgMetadaAccounting = {
  createInvoicingItemsStandardRequestBodies: CreateInvoicingItemsStandardRequestBodiesMetadaAccounting;
};

export type GetCalculatePercentageApiResMetadaAccounting = {
  discountValue: string;
  originalValue: string;
  percent: string;
};
export type GetCalculatePercentageApiArgMetadaAccounting = {
  discountValue: string;
  originalValue: string;
};

export type GetCalculateByPercentageApiResMetadaAccounting = {
  discountValue: string;
  originalValue: string;
  percent: string;
};
export type GetCalculateByPercentageApiArgMetadaAccounting = {
  percent: string;
  originalValue: string;
};

export type InvoicingDocumentAccountingItemTypeMetadaAccounting =
  | 'standard'
  | 'margin'
  | 'discount';
export type GeneratedItemTypeMetadaAccounting = 'standard' | 'proforma_deduction';
export type InvoicingDocumentItemDiscountRequestBodyMetadaAccounting = {
  relatedItemId: string;
  percent: string;
  originalPrice: string;
  priceAfterDiscount: string;
  discountValue: string;
  currency: Alpha3;
};
export type InvoicingDocumentItemResponseBodyMetadaAccounting = {
  itemId: string;
  description: string;
  quantity: string;
  unit: string;
  pricePerUnit: Money;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
  currency: Alpha3;
  totalPrice: VatPriceResponseBodyMetadaAccounting;
  type: InvoicingDocumentAccountingItemTypeMetadaAccounting;
  generatedItemType: GeneratedItemTypeMetadaAccounting;
  relatedItemId: string | null;
  discount: InvoicingDocumentItemDiscountRequestBodyMetadaAccounting | null;
};
export type PaymentStateResponseBodyMetadaAccounting =
  | 'draft'
  | 'canceled'
  | 'completed'
  | 'confirmed'
  | 'partially_paid'
  | 'overpayment'
  | 'reopened'
  | 'overdue';

type CustomerResponseBody = {
  id: string;
  name: string | null;
  tradeName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
  printLanguage: string | null;
};

export type TaxDocumentForCalculationItemTypeMetadaAccounting =
  | 'standard'
  | 'additionalCalculation';
type TaxDocumentCalculationItemResponseBody = {
  reference: string;
  relatedItemId?: string;
  quantity: string;
  vatType: VatType;
  unitPrice: Money;
  isUnitPriceWithVat: boolean;
  totalPriceWithoutVat: Money;
  totalPriceWithVat: Money;
  type: TaxDocumentForCalculationItemTypeMetadaAccounting;
};
export type TaxDocumentForCalculationSummaryItemTypeMetadaAccounting =
  | 'documentPriceWithVatRounding'
  | 'documentPriceWithVatRoundingVat'
  | 'documentPriceWithVatRoundingWithoutVat';

export type RoundTypeMetadaAccounting = 'UP' | 'DOWN' | 'ALGEBRAIC';

type TaxDocumentCalculationResponseBody = {
  taxableSupplyDate: string;
  vatTypes: VatType[];
  vatTypesSummary: {
    [key: string]: {
      groupSummary: PriceResponseBody;
      additionalCalculation: PriceResponseBody | null;
      vatRate: string;
    };
  };
  priceWithVat: Money;
  priceWithoutVat: Money;
  priceWithVatBeforeRounding: Money;
  vat: Money;
  items: TaxDocumentCalculationItemResponseBody[];
  summaryItems: TaxDocumentCalculationSummaryItemResponseBody[];
  usedRoundingDefinitionAndValidity: RoundingDefinitionAndValidityResponseBody | null;
};

export type AdditionalCustomerResponseBodyMetadaAccounting = {
  customerId: string | null;
  contractInformationId: string;
  type: 'operator' | 'recipient';
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
  phoneNumber: PhoneNumber | null;
} | null;
export type ExchangeRateRatioBodyMetadaAccounting = {
  amount: number;
  ratio: string;
  currency: Alpha3;
} | null;
export type InvoiceV3ResponseBodyMetadaAccounting = {
  id: string;
  number: string;
  items: InvoicingDocumentItemResponseBodyMetadaAccounting[];
  paymentState: PaymentStateResponseBodyMetadaAccounting;
  paymentPrescriptionId: string;
  branchId: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string;
  dueSinceIssueDateInDays: number;
  issueDate: string;
  author: string;
  createdAt: string;
  createdById: string;
  sentAt: string | null;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  cancellable: boolean;
  supplier: SupplierResponseBody;
  customer: CustomerResponseBody;
  paymentInfo: PaymentInfoResponseBody;
  totalPriceWithVat: Money;
  totalPriceWithoutVat: Money;
  documentSummary: TaxDocumentCalculationResponseBody;
  additionalCustomer: AdditionalCustomerResponseBodyMetadaAccounting | null;
  customFields: CustomFieldValue[];
  exchangeRateRatio: ExchangeRateRatioBodyMetadaAccounting | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBody | null;
  invoiceCategory: 'default' | 'internal' | null;
};
export type BalanceInvoiceV2ResponseBodyMetadaAccounting = {
  invoice: InvoiceV3ResponseBodyMetadaAccounting;
  proformaIds: string[];
};

export type InvoicingDocumentItemRequestBodyMetadaAccounting = {
  itemId: string;
  description: string;
  quantity: string;
  unit: string;
  pricePerUnit: string;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
  currency: Alpha3;
  type: InvoicingDocumentAccountingItemTypeMetadaAccounting;
  relatedItemId?: string | null;
  discount?: InvoicingDocumentItemDiscountRequestBodyMetadaAccounting | null;
  correctedItemId?: string | null;
};
export type AdditionalCustomerRequestBodyMetadaAccounting = {
  customerId: string;
  contractInformationId: string;
  type: 'operator' | 'recipient';
} | null;
export type CorrectiveTaxDocumentV3ResponseBodyMetadaAccounting = {
  id: string;
  invoicingDocumentId: string;
  invoicingDocumentType: InvoicingDocumentTypeMetadaAccounting;
  invoicingDocumentNumber: string;
  number: string;
  items: InvoicingDocumentItemResponseBodyMetadaAccounting[];
  issueDate: string;
  dueDate: string;
  dateOfTaxableSupply: string;
  dateOfReception: string;
  dueSinceIssueDateInDays: number;
  paymentState: PaymentStateResponseBodyMetadaAccounting;
  author: string;
  createdAt: string;
  customer: CustomerResponseBody;
  totalPriceWithVat: Money;
  totalPriceWithoutVat: Money;
  paymentInfo: PaymentInfoResponseBody;
  isFull: boolean;
  supplier: SupplierResponseBody;
  currency: Alpha3;
  description: string;
  reason: string;
  behindItemsNote: string | null;
  internalNote: string | null;
  paymentPrescriptionId: string;
  branchId: string;
  documentSummary: TaxDocumentCalculationResponseBody;
  sentAt: string | null;
  cancelledAt: string | null;
  cancellable: boolean;
  additionalCustomer: AdditionalCustomerResponseBodyMetadaAccounting | null;
  customFields: CustomFieldValue[];
  exchangeRateRatio: ExchangeRateRatioBodyMetadaAccounting | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBody | null;
  invoiceCategory: 'default' | 'internal' | null;
};
type CustomerRequestBody = {
  id: string | null;
  name: string | null;
  tradeName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zip: string | null;
  country: Alpha3 | null;
  cin: string | null;
  tin: string | null;
};
export type InvoiceDraftItemV2RequestBodyMetadaAccounting = {
  itemId: string;
  description?: string | null;
  quantity?: string | null;
  unit?: string | null;
  pricePerUnit?: string | null;
  isUnitPriceWithVat?: boolean | null;
  vatType?: VatType | null;
  currency?: Alpha3 | null;
  type: InvoicingDocumentAccountingItemTypeMetadaAccounting;
  relatedItemId?: string | null;
  discount?: InvoicingDocumentItemDiscountRequestBodyMetadaAccounting | null;
};
export type InvoiceDraftV2RequestBodyMetadaAccounting = {
  branchId: string;
  issueDate?: string | null;
  dueDate?: string | null;
  dateOfTaxableSupply?: string | null;
  currency?: Alpha3 | null;
  note?: string | null;
  footerNote?: string | null;
  internalNote?: string | null;
  items: InvoiceDraftItemV2RequestBodyMetadaAccounting[];
  billingInformationId: string;
  customer?: CustomerRequestBody | null;
  paymentInfo?: PaymentInfoRequestBodyMetadaAccounting | null;
  additionalCustomer?: AdditionalCustomerRequestBodyMetadaAccounting | null;
  customFields?: FieldValue[];
  exchangeRateRatio?: ExchangeRateRatioBodyMetadaAccounting | null;
};

export type InvoiceDraftItemV2ResponseBodyMetadaAccounting = {
  itemId: string;
  description: string | null;
  quantity: string | null;
  unit: string | null;
  pricePerUnit: MoneyDraftResponseBodyMetadaAccounting | null;
  isUnitPriceWithVat: boolean | null;
  vatType: VatType | null;
  currency: Alpha3 | null;
  type: InvoicingDocumentAccountingItemTypeMetadaAccounting;
  relatedItemId: string | null;
  discount: InvoicingDocumentItemDiscountRequestBodyMetadaAccounting | null;
};

export type InvoiceDraftV2ResponseBodyMetadaAccounting = {
  id: string;
  items: InvoiceDraftItemV2ResponseBodyMetadaAccounting[];
  dueSinceIssueDateInDays: number;
  branchId: string;
  dateOfTaxableSupply: string | null;
  currency: Alpha3 | null;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string | null;
  issueDate: string | null;
  author: string;
  createdById: string;
  createdAt: string;
  supplier: SupplierDraftResponseBodyMetadaAccounting | null;
  customer: CustomerDraftResponseBodyMetadaAccounting | null;
  paymentInfo: PaymentInfoDraftResponseBodyMetadaAccounting | null;
  additionalCustomer: AdditionalCustomerResponseBodyMetadaAccounting | null;
  customFields: FieldValue[];
  exchangeRateRatio: ExchangeRateRatioBodyMetadaAccounting | null;
};
export type InvoiceProformaV2RequestBodyMetadaAccounting = {
  items: InvoicingDocumentItemRequestBodyMetadaAccounting[];
  documentSeriesId: string;
  footerNote?: string | null;
  internalNote?: string | null;
  paid?: boolean;
  markAsSent?: boolean;
  branchId: string;
  issueDate?: string | null;
  dateOfTaxableSupply?: string | null;
  currency: Alpha3;
  note?: string | null;
  billingInformationId: string;
  customer: CustomerRequestBody;
  dueDate: string;
  paymentInfo: PaymentInfoRequestBodyMetadaAccounting;
  additionalCustomer?: AdditionalCustomerRequestBodyMetadaAccounting | null;
  customFields?: FieldValue[];
  exchangeRateRatio?: ExchangeRateRatioBodyMetadaAccounting | null;
};
export type InvoiceProformaV2ResponseBodyMetadaAccounting = {
  id: string;
  number: string;
  items: InvoicingDocumentItemResponseBodyMetadaAccounting[];
  paymentState: PaymentStateResponseBodyMetadaAccounting;
  paymentPrescriptionId: string;
  branchId: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string;
  dueSinceIssueDateInDays: number;
  issueDate: string;
  author: string;
  createdById: string;
  createdAt: string;
  sentAt: string | null;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  cancellable: boolean;
  supplier: SupplierResponseBody;
  customer: CustomerResponseBody;
  paymentInfo: PaymentInfoResponseBody;
  totalPriceWithVat: Money;
  totalPriceWithoutVat: Money;
  documentSummary: TaxDocumentCalculationResponseBody;
  additionalCustomer: AdditionalCustomerResponseBodyMetadaAccounting | null;
  customFields: CustomFieldValue[];
  exchangeRateRatio: ExchangeRateRatioBodyMetadaAccounting | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBody | null;
};
export type CreateInvoicingItemDiscountDefinitionRequestBodyMetadaAccounting = {
  percent: string;
  description: string;
};
export type CreateInvoicingItemDiscountRequestBodyMetadaAccounting = {
  originalItem: InvoicingDocumentItemRequestBodyMetadaAccounting;
  discount: CreateInvoicingItemDiscountDefinitionRequestBodyMetadaAccounting;
};
export type CreateInvoicingItemMarginDefinitionRequestBodyMetadaAccounting = {
  description: string;
  price: string;
};
export type CreateInvoicingItemMarginRequestBodyMetadaAccounting = {
  originalItem: InvoicingDocumentItemRequestBodyMetadaAccounting;
  margin: CreateInvoicingItemMarginDefinitionRequestBodyMetadaAccounting;
};
export type CreateInvoicingItemStandardRequestBodyMetadaAccounting = {
  description: string;
  quantity: string;
  unit: string;
  pricePerUnit: string;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
  currency: Alpha3;
};
export type CreateInvoicingItemsStandardRequestBodiesMetadaAccounting = {
  invoicingItems: CreateInvoicingItemStandardRequestBodyMetadaAccounting[];
};
export type AccPostConfigurationInvoiceCostItemApiResponseMetadaAccounting =
  | /** status 200 Standard response */ ({
      id?: string | null;
    } | null)
  | /** status 204 Standard response */ undefined;
export type AccPostConfigurationInvoiceCostItemApiArgMetadaAccounting = {
  branchId: string | null | undefined;
  generateCostItem: boolean | null;
};
export type AccGetConfigurationInvoiceCostItemApiResponseMetadaAccounting =
  | /** status 200 Standard response */ ({
      branchId?: string | null;
      generateCostItem?: boolean | null;
      application?: string | null;
    } | null)
  | /** status 204 Standard response */ undefined;
export type AccGetConfigurationInvoiceCostItemApiArgMetadaAccounting = {
  id: string;
};
export type AccPatchConfigurationInvoiceCostItemApiResponseMetadaAccounting =
  /** status 204 Standard response */ undefined;
export type AccPatchConfigurationInvoiceCostItemApiArgMetadaAccounting = {
  id: string | null;
  branchId?: string | null | undefined;
  generateCostItem: boolean | null;
};
export type AccDeleteConfigurationApiResponseMetadaAccounting =
  /** status 204 Standard response */ undefined;
export type AccDeleteConfigurationApiArgMetadaAccounting = {
  id: string;
};

export type AccGetConfigurationOperationIdDefaultApiResponse =
  | /** status 200 Standard response */ ({
      operationIdDefault?: {
        id?: string | null;
        itemTypeCode?: string | null;
        itemTypeName?: string | null;
        value?: string | null;
      } | null;
    } | null)
  | /** status 204 Standard response */ void;
export type AccGetConfigurationOperationIdDefaultApiArg = {
  id: string | null;
};
export type AccPatchConfigurationOperationIdDefaultApiResponse =
  /** status 204 Standard response */ void;
export type AccPatchConfigurationOperationIdDefaultApiArg = {
  id: string | null;
  operationId: string | null;
};

export type AccPostMatrixConfigInitializeApiResponseMetadaAccounting =
  | Record<string, unknown>
  | undefined;

export type AccPostMatrixConfigInitializeApiArgMetadaAccounting = void;

export type AccGetConfigurationExportAbraApiResponseMetadaAccounting =
  | /** status 200 Standard response */ ({
      isEnabled?: boolean | null;
      mappingMethod?: {id?: string | null; value?: string | null} | null;
      abraAccount?: {id?: string | null; value?: string | null} | null;
      abraCompany?: {id?: string | null; value?: string | null} | null;
      abraCredentials?: {username?: string | null; password?: string | null} | null;
    } | null)
  | /** status 204 Standard response */ undefined;
export type AccGetConfigurationExportAbraApiArgMetadaAccounting = void;
export type AccPatchConfigurationExportAbraApiResponseMetadaAccounting =
  /** status 204 Standard response */ undefined;
export type AccPatchConfigurationExportAbraApiArgMetadaAccounting = {
  disable?: boolean;
  body?: {
    resource?: {
      abraAccount: string | null;
      abraCompany: string | null;
      mappingMethod: string | null;
    } | null;
    credentials?: {
      username: string | null;
      password: string | null;
    } | null;
  };
};

export type AccGetConfigurationExportCarisApiResponseMetadaAccounting =
  | /** status 200 Standard response */ ({
      exportNewOnly?: ({
        id?: string | null;
        filterValue?: string | null;
        parameterValue?: boolean | null;
      } | null)[];
      exportDateOffset?: ({
        id?: string | null;
        filterValue?: string | null;
        parameterValue?: number | null;
      } | null)[];
    } | null)
  | /** status 204 Standard response */ undefined;
export type AccGetConfigurationExportCarisApiArgMetadaAccounting = void;
export type AccPatchConfigurationExportCarisApiResponseMetadaAccounting =
  /** status 204 Standard response */ undefined;
export type AccPatchConfigurationExportCarisApiArgMetadaAccounting = {
  exportNewOnly?: ({
    id: string;
    parameterValue: boolean | null;
  } | null)[];
  exportDateOffset?: ({
    id: string;
    parameterValue: number | null;
  } | null)[];
} | null;

export type AccGetConfigurationInvoiceCostItemPlusApiResponse =
  | /** status 200 Standard response */ ({
      include?: boolean | null;
    } | null)
  | /** status 204 Standard response */ void;
export type AccGetConfigurationInvoiceCostItemPlusApiArg = void;
export type AccPatchConfigurationInvoiceCostItemPlusApiResponse =
  /** status 204 Standard response */ void;
export type AccPatchConfigurationInvoiceCostItemPlusApiArg = {
  include: boolean | null;
};

export type AccPostConfigurationAuthorizationOperationIdApiResponse =
  /** status 204 Standard response */ void;
export type AccPostConfigurationAuthorizationOperationIdApiArg = {
  body: {
    authorizationId: string;
    operationId: string | null;
  } | null;
};
export type AccGetConfigurationAuthorizationOperationIdApiResponse =
  | /** status 200 Standard response */ ({
      authorizationId?: string | null;
      operationId?: string | null;
    } | null)
  | /** status 204 Standard response */ void;
export type AccGetConfigurationAuthorizationOperationIdApiArg = {
  id: string;
};
export type AccPatchConfigurationAuthorizationOperationIdApiResponse =
  /** status 204 Standard response */ void;
export type AccPatchConfigurationAuthorizationOperationIdApiArg = {
  id: string | null;
  authorizationId: string;
  operationId: string | null;
};
export type AccGetConfigurationAuthorizationsApiResponse =
  /** status 200 Standard response */
  | ({
      id?: string | null;
      name?: string | null;
      code?: string | null;
      nationalSalesCompanyCountry?: string | null;
    } | null)[]
  | /** status 204 Standard response */ void;

export type AccPostDeleteErrorLogsApiArgMetadaAccounting = {
  id: number[];
  deleteAll?: boolean;
};

export type AccPostDeleteErrorLogsApiResponseMetadaAccounting = void;

export type CreatePreAccountingProcessingSettingsApiResponseMetadaAccounting =
  PreAccountingProcessingSettingsResponseBodyMetadaAccounting;
export type CreatePreAccountingProcessingSettingsApiArgMetadaAccounting = {
  createPreAccountingProcessingSettingsRequestBody: CreatePreAccountingProcessingSettingsRequestBodyMetadaAccounting;
};
export type DeletePreAccountingProcessingSettingsApiResponseMetadaAccounting = unknown;
export type DeletePreAccountingProcessingSettingsApiArgMetadaAccounting = {
  id: string;
};
export type GetPreAccountingProcessingSettingsApiResponseMetadaAccounting =
  PreAccountingProcessingSettingsResponseBodyMetadaAccounting;
export type GetPreAccountingProcessingSettingsApiArgMetadaAccounting = {
  id: string;
};
export type UpdatePreAccountingProcessingSettingsApiResponseMetadaAccounting =
  PreAccountingProcessingSettingsResponseBodyMetadaAccounting;
export type UpdatePreAccountingProcessingSettingsApiArgMetadaAccounting = {
  id: string;
  updatePreAccountingProcessingSettingsRequestBody: UpdatePreAccountingProcessingSettingsRequestBodyMetadaAccounting;
};
export type GetListPreAccountingProcessingSettingsApiResponseMetadaAccounting =
  PreAccountingProcessingSettingsResponseBodyMetadaAccounting[];
export type GetListPreAccountingProcessingSettingsApiArgMetadaAccounting = void;
export type PreAccountingProcessingSettingsResponseBodyMetadaAccounting = {
  id: string;
  branchId: string | null;
  notifyAccountingDocuments: boolean;
  notifyStockReceipts: boolean;
  notifyStockIssues: boolean;
  notifyStockTransfers: boolean;
  isSystem: boolean;
};
export type CreatePreAccountingProcessingSettingsRequestBodyMetadaAccounting = {
  branchId: string;
  notifyAccountingDocuments: boolean;
  notifyStockReceipts: boolean;
  notifyStockIssues: boolean;
  notifyStockTransfers: boolean;
};
export type UpdatePreAccountingProcessingSettingsRequestBodyMetadaAccounting = {
  branchId: string | null;
  notifyAccountingDocuments: boolean;
  notifyStockReceipts: boolean;
  notifyStockIssues: boolean;
  notifyStockTransfers: boolean;
};
export type AccM2StrediskoGetCentresApiResponseMetadaAccounting =
  | /** status 200 Standard response */ ({
      m2centres?: ({
        name?: string | null;
        code?: string | null;
        validFrom?: string | null;
        validTo?: string | null;
      } | null)[];
    } | null)
  | /** status 204 Standard response */ void;
export type AccM2StrediskoGetCentresApiArgMetadaAccounting = {
  /** uctovnyDatum */
  accountingDate?: string | null;
  /** ucetPokladne */
  code?: string | null;
};
export type ModuleSourceEnumMetadaAccounting =
  | 'accounting'
  | 'business-case'
  | 'service-case'
  | 'sale-vehicle'
  | 'service-direct';
export type ServiceSourceEnumMetadaAccounting = 'omnetic' | 'caris';
export type DocumentSourceEnumMetadaAccounting = 'internal' | 'external';
export type DocumentSourceRequestBodyMetadaAccounting = {
  documentSource: DocumentSourceEnumMetadaAccounting;
  moduleSource: ModuleSourceEnumMetadaAccounting;
  serviceSource: ServiceSourceEnumMetadaAccounting;
  /** Id of the created document */
  moduleSourceId: string | null;
};
export type DocumentSourceResponseBodyMetadaAccounting = {
  /** Internal (from omnetic) or external (from metada etc.) */
  documentSource: DocumentSourceEnumMetadaAccounting | null;
  /** Module where the document is created. Ex: Accounting, BusinessCase, ServiceCase etc. */
  moduleSource: ModuleSourceEnumMetadaAccounting | null;
  /** Service where the document is created. Omnetic or Caris */
  serviceSource: ServiceSourceEnumMetadaAccounting | null;
  /** Id of the created document */
  moduleSourceId: string | null;
};
export type PaymentInfoRequestBodyV2MetadaAccounting = {
  paymentMethod:
    | 'BANK_TRANSFER'
    | 'CARD'
    | 'CASH'
    | 'CASH_ON_DELIVERY'
    | 'OFFSET'
    | 'FREE_OF_CHARGE';
  bankAccount?: string | null;
  bankName?: string | null;
  iban?: string | null;
  swift?: string | null;
  variableSymbol?: string | null;
  constantSymbol?: string | null;
  specificSymbol?: string | null;
};
type VatTypeSummaryResponseBody = {
  groupSummary: PriceResponseBody;
  additionalCalculation: PriceResponseBody | null;
  vatRate: string;
};
export type RoundingDefinitionAndValidityResponseBodyReadMetadaAccounting = {
  definition: RoundingDefinitionResponseBodyReadMetadaAccounting;
  validity: ValidityResponseBody;
};
export type TaxDocumentCalculationResponseBodyReadMetadaAccounting = {
  taxableSupplyDate: string;
  vatTypes: VatType[];
  vatTypesSummary: {
    [key: string]: VatTypeSummaryResponseBody;
  };
  priceWithVat: MoneyResponseBody;
  priceWithoutVat: MoneyResponseBody;
  priceWithVatBeforeRounding: MoneyResponseBody;
  vat: MoneyResponseBody;
  items: TaxDocumentCalculationItemResponseBody[];
  summaryItems: TaxDocumentCalculationSummaryItemResponseBody[];
  usedRoundingDefinitionAndValidity: RoundingDefinitionAndValidityResponseBodyReadMetadaAccounting | null;
};
export type CorrectiveTaxDocumentItemV2RequestBodyMetadaAccounting = {
  correctedItemId: string;
  description: string;
  quantity: Decimal;
  unit: string;
  pricePerUnit: Decimal;
  isUnitPriceWithVat: boolean;
  vatType: VatType;
  currency: Alpha3;
  itemId: string;
  type: InvoicingDocumentAccountingItemTypeMetadaAccounting;
  relatedItemId: string | null;
  discount?: InvoicingDocumentItemDiscountRequestBodyMetadaAccounting | null;
};
export type InvoiceProformaV4RequestBodyMetadaAccounting = {
  items: InvoicingDocumentItemRequestBodyMetadaAccounting[];
  documentSeriesId: string;
  footerNote?: string | null;
  internalNote?: string | null;
  paid?: boolean;
  markAsSent?: boolean;
  branchId: string;
  issueDate?: string | null;
  dateOfTaxableSupply?: string | null;
  currency: Alpha3;
  note?: string | null;
  billingInformationId: string;
  customer: CustomerRequestBody;
  dueDate: string;
  paymentInfo: PaymentInfoRequestBodyMetadaAccounting;
  additionalCustomer?: AdditionalCustomerRequestBodyMetadaAccounting | null;
  customFields?: FieldValue[];
  exchangeRateRatio?: ExchangeRateRatioBodyMetadaAccounting | null;
  documentSource: DocumentSourceRequestBodyMetadaAccounting;
};
export type CreateCashReceiptV4RequestBodyMetadaAccounting = {
  cashRegisterId: string;
  billingInformationId: string;
  type: CashRegisterDocumentTypeEnum;
  kindOfPayment: CashRegisterDocumentKindOfPaymentEnum;
  issuedAt: string;
  /** When true then the document is paid by card. Balance is not updated. CardTransaction is created and paired to the document. */
  paidByCard: boolean;
  variableSymbol?: string | null;
  customerId?: string | null;
  purpose?: string | null;
  note?: string | null;
  items: CreateCashRegisterDocumentItemRequestBody[];
  /** ID of branch for obtaining DocumentLimitDefinition, or null for Tenant */
  branchId: string | null;
  customFields?: FieldValue[];
  exchangeRateRatio?: ExchangeRateRatioBodyMetadaAccounting;
  relatedInvoicingDocumentId?: string | null;
  relatedInvoicingDocumentType?: InvoicingDocumentTypeUnionMetadaAccounting | null;
  documentSource: DocumentSourceRequestBodyMetadaAccounting;
};
export type CreateCashReceiptV4ApiResponseMetadaAccounting = /** status 200  */ UuidResponseBody;
export type CreateCashReceiptV4ApiArgMetadaAccounting = {
  createCashRegisterDocumentRequestBody: CreateCashReceiptV4RequestBodyMetadaAccounting;
};
export type CreateInvoiceProformaV4ApiResponseMetadaAccounting =
  /** status 200  */ UuidResponseBody;
export type CreateInvoiceProformaV4ApiArgMetadaAccounting = {
  invoiceProformaV4RequestBody: InvoiceProformaV4RequestBodyMetadaAccounting;
};
export type CorrectiveTaxDocumentV4RequestBodyMetadaAccounting = {
  documentSeriesId: string;
  invoicingDocumentId: string;
  invoicingDocumentType: InvoicingDocumentTypeMetadaAccounting;
  issueDate: string;
  dueDate: string;
  dateOfTaxableSupply: string;
  dateOfReception: string;
  description: string;
  reason: string;
  behindItemsNote: string | null;
  internalNote: string | null;
  paymentInfo: PaymentInfoRequestBodyV2MetadaAccounting;
  items: CorrectiveTaxDocumentItemV2RequestBodyMetadaAccounting[];
  additionalCustomer?: AdditionalCustomerRequestBodyMetadaAccounting | null;
  customFields?: CustomFieldValue[];
  exchangeRateRatio?: ExchangeRateRatioBodyMetadaAccounting | null;
  invoiceCategory?: string | null;
  documentSource: DocumentSourceRequestBodyMetadaAccounting;
};
export type CreateCorrectiveTaxDocumentV4ApiResponseMetadaAccounting =
  /** status 200  */ UuidResponseBody;
export type CreateCorrectiveTaxDocumentV4ApiArgMetadaAccounting = {
  correctiveTaxDocumentV4RequestBody: CorrectiveTaxDocumentV4RequestBodyMetadaAccounting;
};
export type InvoiceProformaV4ResponseBodyReadMetadaAccounting = {
  id: string;
  number: string;
  items: InvoicingDocumentItemResponseBodyMetadaAccounting[];
  paymentState: PaymentStateResponseBodyMetadaAccounting;
  paymentPrescriptionId: string;
  branchId: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string;
  dueSinceIssueDateInDays: number;
  issueDate: string;
  author: string;
  createdById: string;
  createdAt: string;
  sentAt: string | null;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  cancellable: boolean;
  supplier: SupplierResponseBody;
  customer: CustomerResponseBody;
  paymentInfo: PaymentInfoResponseBody;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  documentSummary: TaxDocumentCalculationResponseBodyReadMetadaAccounting;
  additionalCustomer: AdditionalCustomerResponseBodyMetadaAccounting | null;
  customFields: CustomFieldValue[];
  exchangeRateRatio: ExchangeRateRatioBodyMetadaAccounting | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBodyReadMetadaAccounting | null;
  documentSource: DocumentSourceResponseBodyMetadaAccounting;
};
export type GetInvoiceProformaV4ApiResponseMetadaAccounting =
  /** status 200  */ InvoiceProformaV4ResponseBodyReadMetadaAccounting;
export type GetInvoiceProformaV4ApiArgMetadaAccounting = {
  invoiceProformaId: string;
};
export type PaymentInfoResponseBodyV2 = {
  paymentMethod: 'BANK_TRANSFER' | 'CARD' | 'CASH' | 'OFFSET' | 'FREE_OF_CHARGE';
  bankAccount: string | null;
  bankName: string | null;
  iban: string | null;
  swift: string | null;
  variableSymbol: string | null;
  constantSymbol: string | null;
  specificSymbol: string | null;
};

export type InvoiceV4ResponseBodyReadMetadaAccounting = {
  id: string;
  number: string;
  items: InvoicingDocumentItemResponseBodyMetadaAccounting[];
  paymentState: PaymentStateResponseBodyMetadaAccounting;
  paymentPrescriptionId: string;
  branchId: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string;
  dueSinceIssueDateInDays: number;
  issueDate: string;
  author: string;
  createdAt: string;
  createdById: string;
  sentAt: string | null;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  cancellable: boolean;
  supplier: SupplierResponseBody;
  customer: CustomerResponseBody;
  paymentInfo: PaymentInfoResponseBodyV2;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  documentSummary: TaxDocumentCalculationResponseBodyReadMetadaAccounting;
  additionalCustomer: AdditionalCustomerResponseBodyMetadaAccounting | null;
  customFields: CustomFieldValue[];
  exchangeRateRatio: ExchangeRateRatioBodyMetadaAccounting | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBodyReadMetadaAccounting | null;
  invoiceCategory: string | null;
  documentSource: DocumentSourceResponseBodyMetadaAccounting;
};
export type InvoiceV4ResponseBodyMetadaAccounting = {
  id: string;
  number: string;
  items: InvoicingDocumentItemResponseBodyMetadaAccounting[];
  paymentState: PaymentStateResponseBodyMetadaAccounting;
  paymentPrescriptionId: string;
  branchId: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  footerNote: string | null;
  internalNote: string | null;
  dueDate: string;
  dueSinceIssueDateInDays: number;
  issueDate: string;
  author: string;
  createdAt: string;
  createdById: string;
  sentAt: string | null;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  cancellable: boolean;
  supplier: SupplierResponseBody;
  customer: CustomerResponseBody;
  paymentInfo: PaymentInfoResponseBodyV2;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  documentSummary: TaxDocumentCalculationResponseBody;
  additionalCustomer: AdditionalCustomerResponseBodyMetadaAccounting | null;
  customFields: FieldValue[];
  exchangeRateRatio: ExchangeRateRatioBodyMetadaAccounting | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBody | null;
  invoiceCategory: string | null;
  documentSource: DocumentSourceResponseBodyMetadaAccounting;
};

export type BalanceInvoiceV4ResponseBodyMetadaAccounting = {
  invoice: InvoiceV4ResponseBodyMetadaAccounting;
  proformaIds: string[];
};
export type BalanceInvoiceV4ResponseBodyReadMetadaAccounting = {
  invoice: InvoiceV4ResponseBodyReadMetadaAccounting;
  proformaIds: string[];
};
export type GetBalanceInvoiceV4ApiResponseMetadaAccounting =
  /** status 200  */ BalanceInvoiceV4ResponseBodyReadMetadaAccounting;
export type GetBalanceInvoiceV4ApiArgMetadaAccounting = {
  balanceInvoiceId: string;
};
export type AccountingPaymentMethodMetadaAccounting =
  | 'UNDEFINED'
  | 'BANK_TRANSFER'
  | 'CARD'
  | 'CASH'
  | 'OFFSET';
export type InvoiceV4RequestBodyMetadaAccounting = {
  items: InvoicingDocumentItemRequestBodyMetadaAccounting[];
  documentSeriesId: string;
  footerNote?: string | null;
  internalNote?: string | null;
  paid?: boolean;
  markAsSent?: boolean;
  branchId: string;
  issueDate?: string | null;
  dateOfTaxableSupply?: string | null;
  currency: Alpha3;
  note?: string | null;
  billingInformationId: string;
  customer: CustomerRequestBody;
  dueDate: string;
  paymentInfo: PaymentInfoRequestBodyV2MetadaAccounting;
  additionalCustomer?: AdditionalCustomerRequestBodyMetadaAccounting | null;
  customFields?: CustomFieldValue[];
  exchangeRateRatio?: ExchangeRateRatioBodyMetadaAccounting | null;
  invoiceCategory?: string | null;
  documentSource: DocumentSourceRequestBodyMetadaAccounting;
};
export type CashReceiptV4ResponseBodyReadMetadaAccounting = {
  id: string;
  number: string;
  createdAt: string;
  createdById: string;
  cashRegisterId: string;
  paymentTransactionId: string | null;
  type: CashRegisterDocumentTypeEnum;
  kindOfPayment: CashRegisterDocumentKindOfPaymentEnum;
  state: CashRegisterDocumentStateEnum;
  issuedAt: string;
  isTaxDocument: boolean;
  cancelledDocumentId: string | null;
  relatedInvoicingDocumentNumber: string | null;
  variableSymbol: string | null;
  customerId: string | null;
  purpose: string | null;
  note: string | null;
  cashRegisterBalanceBefore: Money | null;
  cashRegisterBalanceAfter: Money | null;
  totalPriceWithoutVat: Money;
  totalPriceWithVat: Money;
  items: CashRegisterDocumentItemResponseBody[];
  issuer: IssuerResponseBody;
  cashReceiptSummary: TaxDocumentCalculationResponseBodyReadMetadaAccounting;
  paymentMethod: AccountingPaymentMethodMetadaAccounting;
  customFields: FieldValue[];
  exchangeRateRatio: ExchangeRateRatioBodyMetadaAccounting;
  exchangeRateRatioCashReceiptSummary: TaxDocumentCalculationResponseBodyReadMetadaAccounting | null;
  relatedInvoicingDocumentId: string | null;
  relatedInvoicingDocumentType: InvoicingDocumentTypeMetadaAccounting | null;
  documentSource: DocumentSourceResponseBodyMetadaAccounting;
};
export type GetCashReceiptV4ApiResponseMetadaAccounting =
  /** status 200  */ CashReceiptV4ResponseBodyReadMetadaAccounting;
export type GetCashReceiptV4ApiArgMetadaAccounting = {
  cashRegisterDocumentId: string;
};
export type CorrectiveTaxDocumentTypeEnumMetadaAccounting =
  | 'full-correction'
  | 'partial-correction';
export type CorrectiveTaxDocumentV5ResponseBodyReadMetadaAccounting = {
  id: string;
  invoicingDocumentId: string;
  invoicingDocumentType: InvoicingDocumentTypeMetadaAccounting;
  invoicingDocumentNumber: string;
  number: string;
  items: InvoicingDocumentItemResponseBodyMetadaAccounting[];
  issueDate: string;
  dueDate: string;
  dateOfTaxableSupply: string;
  dateOfReception: string;
  dueSinceIssueDateInDays: number;
  paymentState: PaymentStateResponseBodyMetadaAccounting;
  author: string;
  createdAt: string;
  customer: CustomerResponseBody;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  paymentInfo: PaymentInfoResponseBodyV2;
  supplier: SupplierResponseBody;
  currency: Alpha3;
  description: string;
  reason: string;
  behindItemsNote: string | null;
  internalNote: string | null;
  paymentPrescriptionId: string;
  branchId: string;
  documentSummary: TaxDocumentCalculationResponseBodyReadMetadaAccounting;
  sentAt: string | null;
  cancelledAt: string | null;
  cancellable: boolean;
  additionalCustomer: AdditionalCustomerResponseBodyMetadaAccounting | null;
  customFields: CustomFieldValue[];
  exchangeRateRatio: ExchangeRateRatioBodyMetadaAccounting | null;
  exchangeRateRatioDocumentSummary: TaxDocumentCalculationResponseBodyReadMetadaAccounting | null;
  invoiceCategory: string | null;
  documentSource: DocumentSourceResponseBodyMetadaAccounting;
  correctiveTaxDocumentType: CorrectiveTaxDocumentTypeEnumMetadaAccounting;
};
export type GetCorrectiveTaxDocumentV5ApiResponseMetadaAccounting =
  CorrectiveTaxDocumentV5ResponseBodyReadMetadaAccounting;
export type GetCorrectiveTaxDocumentV5ApiArgMetadaAccounting = {
  correctiveTaxDocumentId: string;
};

export type CreateInvoiceV4ApiResponseMetadaAccounting = UuidResponseBody;
export type CreateInvoiceV4ApiArgMetadaAccounting = {
  invoiceV4RequestBody: InvoiceV4RequestBodyMetadaAccounting;
};

export type GetInvoiceV4ApiResponseMetadaAccounting = InvoiceV4ResponseBodyReadMetadaAccounting;
export type GetInvoiceV4ApiArgMetadaAccounting = {
  invoiceId: string;
};
export type GetBalanceInvoiceByInvoiceProformaIdV4ApiResponseMetadaAccounting =
  BalanceInvoiceV4ResponseBodyReadMetadaAccounting;
export type GetBalanceInvoiceByInvoiceProformaIdV4ApiArgMetadaAccounting = {
  invoiceProformaId: string;
};
type ValidityResponseBody = {
  /** Date from which the validity is valid */
  validFrom: string;
  roundType: RoundTypeMetadaAccounting;
  roundStep: RoundStep;
  /** Will be the rounding item taxed or not? */
  taxRounding: boolean;
};
export type RoundingDefinitionResponseBodyReadMetadaAccounting = {
  id: string;
  branchId: string | null;
  paymentType: PaymentType;
  validities: ValidityResponseBody[];
  /** Provides information on whether this is a system definition that cannot be deleted. */
  isSystem: boolean;
};
type RoundStep = '0.01' | '0.05' | '0.1' | '0.5' | '1' | '5' | '10' | '100';

export type CreateCorrectiveTaxDocumentSettingApiResponseMetadaAccounting =
  CorrectiveTaxDocumentSettingResponseBodyMetadaAccounting;
export type CreateCorrectiveTaxDocumentSettingApiArgMetadaAccounting = {
  createCorrectiveTaxDocumentSettingRequestBody: CreateCorrectiveTaxDocumentSettingRequestBodyMetadaAccounting;
};
export type DeleteCorrectiveTaxDocumentSettingApiResponseMetadaAccounting = unknown;
export type DeleteCorrectiveTaxDocumentSettingApiArgMetadaAccounting = {
  id: string;
};
export type GetCorrectiveTaxDocumentSettingApiResponseMetadaAccounting =
  CorrectiveTaxDocumentSettingResponseBodyMetadaAccounting;
export type GetCorrectiveTaxDocumentSettingApiArgMetadaAccounting = {
  id: string;
};
export type UpdateCorrectiveTaxDocumentSettingApiResponseMetadaAccounting =
  CorrectiveTaxDocumentSettingResponseBodyMetadaAccounting;
export type UpdateCorrectiveTaxDocumentSettingApiArgMetadaAccounting = {
  id: string;

  updateCorrectiveTaxDocumentSettingRequestBody: UpdateCorrectiveTaxDocumentSettingRequestBodyMetadaAccounting;
};
export type CreateCorrectiveTaxDocumentTypeMetadaAccounting = 'without_proforma' | 'with_proforma';
export type CorrectiveTaxDocumentSettingResponseBodyMetadaAccounting = {
  id: string;
  branchId: string | null;
  createCorrectiveTaxDocumentType: CreateCorrectiveTaxDocumentTypeMetadaAccounting;
  isSystem: boolean;
};
export type CreateCorrectiveTaxDocumentSettingRequestBodyMetadaAccounting = {
  branchId: string;
  createCorrectiveTaxDocumentType: CreateCorrectiveTaxDocumentTypeMetadaAccounting;
};
export type UpdateCorrectiveTaxDocumentSettingRequestBodyMetadaAccounting = {
  branchId: string | null;
  createCorrectiveTaxDocumentType: CreateCorrectiveTaxDocumentTypeMetadaAccounting;
};
export type AccPatchDocumentExportDateSetNullApiResponseMetadaAccounting =
  | ({
      errors?: ({
        message?: string | null;
        code?: string | null;
      } | null)[];
      validationErrors?: (string | null)[];
    } | null)
  | void;
export type AccPatchDocumentExportDateSetNullApiArgMetadaAccounting = {
  id: string | null;
  type:
    | 'invoice'
    | 'balance-invoice'
    | 'proforma-invoice'
    | 'corrective-tax-document'
    | 'expensive-cash-receipt'
    | 'incoming-cash-receipt'
    | 'tax-document-for-payment';
};
export type TaxDocumentForPaymentV4ResponseBodyReadMetadaAccounting = {
  id: string;
  invoiceProformaId: string;
  userId: string;
  number: string;
  createdAt: string;
  issueDate: string;
  dueDate: string;
  dateOfTaxableSupply: string;
  currency: Alpha3;
  note: string | null;
  internalNote: string | null;
  footerNote: string | null;
  items: InvoicingDocumentItemResponseBodyMetadaAccounting[];
  supplier: SupplierResponseBody;
  customer: CustomerResponseBody;
  paymentInfo: PaymentInfoResponseBody;
  paymentPrescriptionId: string;
  branchId: string;
  cancelledAt: string | null;
  cancelledDocumentId: string | null;
  totalPriceWithVat: MoneyResponseBody;
  totalPriceWithoutVat: MoneyResponseBody;
  taxDocumentSummary: TaxDocumentCalculationResponseBodyReadMetadaAccounting;
  exchangeRateRatio: ExchangeRateRatioBodyMetadaAccounting | null;
  exchangeRateRatioTaxDocumentSummary: TaxDocumentCalculationResponseBodyReadMetadaAccounting | null;
  documentSource: DocumentSourceResponseBodyMetadaAccounting;
};
export type GetTaxDocumentForPaymentV4ApiResponseMetadaAccounting =
  TaxDocumentForPaymentV4ResponseBodyReadMetadaAccounting;
export type GetTaxDocumentForPaymentV4ApiArgMetadaAccounting = {
  id: string;
};

export type AccountingCorrectiveTaxDocumentItemsResponseBodyMetadaAccounting = {
  items: InvoicingDocumentItemResponseBodyMetadaAccounting[];
};
export type AccountingGetCorrectiveTaxDocumentItemsApiResponseMetadaAccounting =
  AccountingCorrectiveTaxDocumentItemsResponseBodyMetadaAccounting;
export type AccountingGetCorrectiveTaxDocumentItemsApiArgMetadaAccounting = {
  balanceInvoiceId: string;
};
export type GetListCorrectiveTaxDocumentSettingApiResponseMetadaAccounting =
  CorrectiveTaxDocumentSettingResponseBodyMetadaAccounting[];
export type GetListCorrectiveTaxDocumentSettingApiArgMetadaAccounting = {};

export type AccItemCodeServiceGetValidCodesApiResponse =
  | ({
      itemCode?: string | null;
      itemCodeName?: string | null;
      applicationId?: string | null;
      validFrom?: string | null;
      /** typPolozky from Caris - NOT a DMS itemType equivalent */
      validTo?: string | null;
      currentlyValid?: boolean | null;
    } | null)[]
  | void;
export type AccItemCodeServiceGetValidCodesApiArg = {
  application: 'used-cars';
};

export type AccGetMatrix202and205ApiResponse = {
  caseType: string[];
  variantType: string[];
};

export type AccGetMatrix202and205ApiArg = {
  orderVariantPart: 'case-type' | 'variant-type';
};

export type AccGetAccountingContextCodeTableIdApiArg = {
  referenceId: string;
};

export type AccGetAccountingContextCodeTableIdApiResponse = {
  referenceId: string | null;
  referenceType: string | null;
  accountingCode: string | null;
};

export type AccPostAccountingContextCodeTableApiArg = {
  attributeName: string;
  body: {
    referenceId: string;
    accountingCode?: string | null;
  } | null;
};

export type AccPostAccountingContextCodeTableApiResponse = {
  referenceId: string | null;
  accountingCode: string;
};
